import React from 'react';
import MarketingLayout from './Layouts/MarketingLayout';
import { Link } from '@inertiajs/react';

const stripHtml = (html) => {
  if (!html) {
    return '';
  }

  if (typeof window === 'undefined') {
    return String(html).replace(/<[^>]*>/g, '');
  }

  const doc = new DOMParser().parseFromString(String(html), 'text/html');
  return doc.body.textContent || '';
};

const academicIntelligenceFlow = {
  tagline: 'Results, approvals, and academic reporting in one governed workflow.',
  heroBullets: [
    'Clear result processing views for teachers, heads of department, and school leadership',
    'Approval checkpoints before report sheets are released to parents or students',
    'Live academic visibility across classes, terms, assessments, and learner performance',
  ],
  outcomes: [
    {
      title: 'Automatic reporting',
      text: 'Turn score entry, grade aggregation, approvals, and report publishing into one governed academic workflow.',
      icon: 'fas fa-chart-column',
    },
    {
      title: 'Informs intervention',
      text: 'Use class and learner performance patterns to spot gaps early and support decisions before the term closes.',
      icon: 'fas fa-user-check',
    },
    {
      title: 'Actionable visibility',
      text: 'Give academic leaders current data for planning, moderation, parent conversations, and management review.',
      icon: 'fas fa-magnifying-glass-chart',
    },
  ],
  workflow: [
    {
      title: 'Collect assessment records',
      text: 'Teachers enter scores through structured gradebooks while departments keep assessment formats consistent across classes.',
      image: '/images/sections/originals/academics_1547200287.jpg',
    },
    {
      title: 'Review before publishing',
      text: 'Academic leads can check submitted records, resolve gaps, and approve results before report sheets are visible.',
      image: '/images/sections/originals/facilities_1547201971.jpg',
    },
    {
      title: 'Know where each learner stands',
      text: 'Class, subject, and learner views help leadership understand performance movement instead of waiting for spreadsheet summaries.',
      image: '/images/sections/originals/gallery_1547198944.jpg',
    },
    {
      title: 'Release reports with controls',
      text: 'Publish reports through the right portal when approvals are complete, with room for school rules such as holds and review windows.',
      image: '/images/sections/originals/vision_1548936113.jpg',
    },
  ],
  readiness: {
    title: 'Start with one term, then scale',
    text: 'Academic Intelligence works well as a focused pilot: configure classes, assessments, grading rules, approval flow, and report templates for one live academic cycle before expanding to the whole school.',
  },
  quotes: [
    {
      quote: 'The value is not only faster report sheets. It is knowing exactly where every class stands before management meetings.',
      name: 'Academic Director',
      role: 'Secondary school group',
    },
    {
      quote: 'Approval visibility changed the process. Teachers, HODs, and leadership stopped working from different result files.',
      name: 'School Administrator',
      role: 'K12 institution',
    },
  ],
};

const genericFlow = {
  tagline: 'A focused SchoolPlex workflow your school can activate without a full-platform rollout.',
  heroBullets: [
    'Focused rollout for one operational bottleneck',
    'Structured staff workflow with clear ownership',
    'Connects cleanly with other SchoolPlex suites when ready',
  ],
  outcomes: [
    {
      title: 'Clear workflow',
      text: 'Give staff a defined process instead of scattered files, messages, and manual follow-up.',
      icon: 'fas fa-diagram-project',
    },
    {
      title: 'Better decisions',
      text: 'Expose the right operational data to the people responsible for review, approval, and action.',
      icon: 'fas fa-chart-line',
    },
    {
      title: 'Practical rollout',
      text: 'Start with the school’s urgent need, prove value, then activate related capabilities over time.',
      icon: 'fas fa-layer-group',
    },
  ],
  workflow: [
    {
      title: 'Set up the operating model',
      text: 'Configure the solution around your school’s roles, term structure, and approval responsibilities.',
      image: '/images/sections/originals/academics_1547197961.jpg',
    },
    {
      title: 'Move staff into one process',
      text: 'Replace ad hoc tracking with one shared workflow that leadership can review as work progresses.',
      image: '/images/sections/originals/facilities_1547199816.jpg',
    },
    {
      title: 'Use data for follow-through',
      text: 'Turn captured records into practical follow-up for administrators, teachers, finance, or leadership teams.',
      image: '/images/sections/originals/gallery_1547199135.jpg',
    },
  ],
  readiness: {
    title: 'Pilot the workflow before expanding',
    text: 'Run the solution with one department, campus, or term cycle first. Once the operating model is stable, expand it into the wider SchoolPlex platform.',
  },
  quotes: [
    {
      quote: 'The strongest benefit was getting one accountable workflow instead of every team solving the same problem differently.',
      name: 'Operations Lead',
      role: 'SchoolPlex pilot school',
    },
  ],
};

export default function SolutionShow({ solution, relatedSolutions, featureHighlights }) {
  const flow = solution?.slug === 'academic-intelligence' ? academicIntelligenceFlow : genericFlow;
  const overview = stripHtml(solution?.overview);
  const modules = solution?.modules || [];
  const highlights = (featureHighlights || []).slice(0, 6);
  const moduleCards = modules.length > 0
    ? modules.map((module) => ({
        id: module.id,
        title: module.label,
        text: module.overview || `${module.label} gives this suite a focused workflow your staff can operate and review from one place.`,
        icon: module.icon || 'fas fa-layer-group',
        features: (module.features || []).slice(0, 4),
      }))
    : flow.outcomes.map((item, index) => ({
        id: item.title,
        title: item.title,
        text: item.text,
        icon: item.icon,
        features: highlights.slice(index * 2, index * 2 + 2),
      }));

  const getWhatsAppMessage = (slug) => {
    switch (slug) {
      case 'academic-intelligence':
        return 'Hello, I want to start with Academic Intelligence on SchoolPlex. We want to simplify results, approvals, and academic reporting this term.';
      case 'schoolplex-prestige':
        return 'Hello, I’m interested in SchoolPlex Prestige to modernize our school website and digital presence. I would like to learn more.';
      case 'student-radar':
        return 'Hello, I’m interested in Student Radar on SchoolPlex to simplify student assessment, grades, and tracking.';
      default:
        return `Hello, I want to learn more about ${solution?.label || 'SchoolPlex solutions'} for my school.`;
    }
  };

  const whatsappUrl = `https://wa.me/2348144676666?text=${encodeURIComponent(getWhatsAppMessage(solution?.slug))}`;

  return (
    <MarketingLayout title={solution?.label}>
      <section className="spx-solution-hero">
        <div className="spx-container">
          <Link href="/solutions" className="spx-solution-back">
            <i className="fas fa-arrow-left"></i>
            Solutions Library
          </Link>

          <div className="spx-solution-hero-grid">
            <div>
              <h1 className="spx-solution-title">{solution?.label}</h1>
              <p className="spx-solution-lead">{flow.tagline}</p>
            </div>

            <div className="spx-dashboard-preview" aria-hidden="true">
              <div className="spx-dashboard-preview__bar">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <div className="spx-dashboard-preview__metrics">
                <div>
                  <strong>94%</strong>
                  <span>Gradebooks submitted</span>
                </div>
                <div>
                  <strong>18</strong>
                  <span>Classes ready</span>
                </div>
                <div>
                  <strong>6</strong>
                  <span>Reviews pending</span>
                </div>
              </div>
              <div className="spx-dashboard-preview__table">
                {['JSS 1A', 'JSS 2B', 'SSS 1 Science', 'SSS 2 Arts'].map((row, index) => (
                  <div key={row}>
                    <span>{row}</span>
                    <span style={{ width: `${88 - index * 9}%` }}></span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="spx-section spx-solution-intro">
        <div className="spx-container">
          <div className="spx-solution-intro-grid">
            <div>
              <h2 className="spx-title-medium">What this suite solves</h2>
              <p>{overview || 'This solution gives your school one structured workflow for an important operational bottleneck, with clear ownership, review visibility, and room to expand when the process is stable.'}</p>
            </div>

            <ul className="spx-solution-bullets">
              {flow.heroBullets.map((item) => (
                <li key={item}>
                  <i className="fas fa-check-circle"></i>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="spx-section spx-solution-modules">
        <div className="spx-container">
          <div className="spx-solution-section-head">
            <h2 className="spx-title-medium">How the modules work together</h2>
            <p>Each card represents a module in this solution. Scroll through the stack to see the operating flow without stretching the page into a long list.</p>
          </div>

          <div className="spx-module-stack">
            {moduleCards.map((module, index) => (
              <article className="spx-module-stack-card" key={module.id} style={{ '--stack-index': index }}>
                <div className="spx-module-stack-card__media">
                  <i className={module.icon}></i>
                  <span>{String(index + 1).padStart(2, '0')}</span>
                </div>
                <div className="spx-module-stack-card__body">
                  <h3>{module.title}</h3>
                  <p>{module.text}</p>
                  {module.features.length > 0 && (
                    <ul>
                      {module.features.map((feature) => (
                        <li key={feature.id || feature.label}>
                          <i className="fas fa-circle-check"></i>
                          <span>{feature.label}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="spx-section">
        <div className="spx-container">
          <div className="spx-solution-section-head">
            <h2 className="spx-title-medium">Track, plan, and support from one workflow</h2>
            <p>
              The suite is designed around the practical sequence school teams already follow: capture records, review quality, understand progress, and release decisions with confidence.
            </p>
          </div>

          <div className="spx-solution-workflow">
            {flow.workflow.map((item, index) => (
              <article className="spx-solution-workflow-row" key={item.title}>
                <img src={item.image} alt="" loading="lazy" />
                <div>
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {(highlights.length > 0 || modules.length > 0) && (
        <section className="spx-section spx-solution-capabilities">
          <div className="spx-container">
            <div className="spx-solution-section-head">
              <h2 className="spx-title-medium">Capabilities included</h2>
              <p>Use these capabilities as the implementation checklist for the suite.</p>
            </div>

            <div className="spx-solution-capability-grid">
              {highlights.map((feature) => (
                <div className="spx-solution-capability" key={feature.label}>
                  <i className={feature.icon || 'fas fa-circle-check'}></i>
                  <strong>{feature.label}</strong>
                  {feature.overview && <span>{feature.overview}</span>}
                </div>
              ))}

              {highlights.length === 0 && modules.map((module) => (
                <div className="spx-solution-capability" key={module.id}>
                  <i className={module.icon || 'fas fa-circle-check'}></i>
                  <strong>{module.label}</strong>
                  {module.overview && <span>{module.overview}</span>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="spx-section">
        <div className="spx-container">
          <div className="spx-solution-readiness">
            <div>
              <h2>{flow.readiness.title}</h2>
              <p>{flow.readiness.text}</p>
            </div>
            <Link href="/contact" className="spx-btn spx-btn-secondary">
              Plan a rollout
            </Link>
          </div>
        </div>
      </section>

      <section className="spx-section spx-solution-proof">
        <div className="spx-container">
          <h2 className="spx-title-medium">What school teams value</h2>
          <div className="spx-solution-quote-grid">
            {flow.quotes.map((item) => (
              <figure className="spx-solution-quote" key={item.quote}>
                <blockquote>{item.quote}</blockquote>
                <figcaption>
                  <strong>{item.name}</strong>
                  <span>{item.role}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {relatedSolutions && relatedSolutions.length > 0 && (
        <section className="spx-section" style={{ borderTop: '1px solid var(--border-primary)' }}>
          <div className="spx-container">
            <div className="spx-solution-section-head">
              <h2 className="spx-title-medium">Explore other suites</h2>
              <p>Extend the rollout with adjacent SchoolPlex workflows when the school is ready.</p>
            </div>

            <div className="spx-related-solutions">
              {relatedSolutions.map((rel) => (
                <Link key={rel.id} href={`/solutions/${rel.slug}`} className="spx-related-solution">
                  <span><i className={rel.icon || 'fas fa-cube'}></i></span>
                  <strong>{rel.label}</strong>
                  <i className="fas fa-chevron-right"></i>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="spx-section">
        <div className="spx-container">
          <div className="spx-solution-final">
            <h2>Want to see {solution?.label} in action?</h2>
            <p>Start with a free workspace or talk through the rollout plan with the SchoolPlex team.</p>
            <div>
              <a href={`/onboarding?solutions=${encodeURIComponent(solution?.slug || '')}`} className="spx-btn spx-btn-primary">
                Start Free
              </a>
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="spx-btn spx-btn-secondary">
                Discuss rollout
              </a>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
